import type { SeasonCampaignSummary } from '@multirx/shared/types/championship.js';
import { createPillButton } from '../ui/pillButton.js';
import { filterPastCampaigns, toSeasonHistoryCardModel } from './seasonHistoryModel.js';

export type SeasonHistoryModalResult = { campaignKey: string } | null;

/**
 * Modale de choix d’une saison passée (`.modal-backdrop` + `.modal`).
 * Escape / backdrop / Fermer → `null` ; clic carte → `{ campaignKey }`.
 */
export function openSeasonHistoryModal(
  host: HTMLElement,
  campaigns: SeasonCampaignSummary[]
): Promise<SeasonHistoryModalResult> {
  return new Promise((resolve) => {
    const past = filterPastCampaigns(campaigns);
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop seasons-history-modal';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', 'seasons-history-modal-title');

    backdrop.innerHTML = `
      <div class="modal seasons-history-modal-dialog">
        <div class="modal-header">
          <h2 id="seasons-history-modal-title">Historique des saisons</h2>
          <button type="button" class="btn btn-ghost seasons-history-modal-close" aria-label="Fermer">×</button>
        </div>
        <div class="modal-body seasons-history-modal-body"></div>
        <div class="modal-footer seasons-history-modal-footer"></div>
      </div>`;

    const body = backdrop.querySelector('.seasons-history-modal-body') as HTMLElement;
    const footer = backdrop.querySelector('.seasons-history-modal-footer') as HTMLElement;

    if (past.length === 0) {
      body.innerHTML =
        '<p class="seasons-history-modal-empty">Aucune saison passée pour l’instant.</p>';
    } else {
      const list = document.createElement('div');
      list.className = 'seasons-history-modal-grid';
      for (const campaign of past) {
        const model = toSeasonHistoryCardModel(campaign);
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'seasons-campaign-card seasons-history-modal-card';
        card.dataset.campaignKey = model.campaignKey;
        card.innerHTML = `
          <div class="seasons-campaign-card-head">
            <h3>${model.title}</h3>
            <span class="seasons-campaign-badge ${model.statusClass}">${model.statusLabel}</span>
          </div>
          <p class="seasons-campaign-card-meta">${model.meta}</p>
          <p class="seasons-campaign-card-action">Voir cette saison →</p>`;
        list.appendChild(card);
      }
      body.appendChild(list);
    }

    const closeBtn = createPillButton('Fermer', { variant: 'soft' });
    footer.appendChild(closeBtn);

    let settled = false;
    const finish = (value: SeasonHistoryModalResult): void => {
      if (settled) return;
      settled = true;
      backdrop.remove();
      resolve(value);
    };

    closeBtn.addEventListener('click', () => finish(null));
    backdrop
      .querySelector('.seasons-history-modal-close')
      ?.addEventListener('click', () => finish(null));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) finish(null);
    });
    backdrop.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') finish(null);
    });
    body.querySelectorAll<HTMLElement>('[data-campaign-key]').forEach((card) => {
      card.addEventListener('click', () => {
        const key = card.dataset.campaignKey;
        if (key) finish({ campaignKey: key });
      });
    });

    host.appendChild(backdrop);
    (closeBtn as HTMLButtonElement).focus();
  });
}
